class MulticolumnBlock extends HTMLElement {
    constructor() {
        super();
        this.multicolumn = this.querySelector(".multicolumn__scrollable");
        this.track = this.querySelector(".multicolumn-scrollbar .scrollbar-track");
        this.thumb = this.querySelector(".multicolumn-scrollbar .scrollbar-thumb");

        // Cast to HTMLElement for style property access
        this.track = /** @type {HTMLElement} */ (this.track);
        this.thumb = /** @type {HTMLElement} */ (this.thumb);
        this.multicolumn = /** @type {HTMLElement} */ (this.multicolumn);
    
        if (!this.multicolumn || !this.track || !this.thumb) return;

        // Init
        this.updateThumbSize();
        this.updateThumbPosition();
    }

    connectedCallback() {
        if (!this.multicolumn || !this.track || !this.thumb) return;

        this.desktopDragEventListeners();
        this.mobileDragEventListeners();

        // Sync on scroll
        this.multicolumn.addEventListener("scroll", this.updateThumbPosition.bind(this));
        window.addEventListener("resize", () => {
            this.updateThumbSize();
            this.updateThumbPosition();
        });

        // === Track click-to-jump logic ===
        this.track.addEventListener("click", (e) => {
            // Don't trigger if user clicks the thumb itself
            if (e.target === this.thumb) return;

            const trackRect = this.track.getBoundingClientRect();
            const clickX = e.clientX - trackRect.left;

            const card = this.multicolumn.querySelector(".multicolumn-card");
            if (!card) return;

            const cardWidth = /** @type {HTMLElement} */ (card).offsetWidth + parseInt(getComputedStyle(this.multicolumn).gap || "0", 10);

            // Decide direction
            const isRight = clickX > this.thumb.offsetLeft + this.thumb.offsetWidth / 2;

            // Scroll by exactly one card
            const scrollAmount = isRight ? cardWidth : -cardWidth;
            this.multicolumn.scrollBy({ left: scrollAmount, behavior: "smooth" });
        });
    }

    desktopDragEventListeners() {
        let isDraggingThumb = false;
        let dragOffsetX = 0;
        let startX = 0;
        let startScrollLeft = 0;

        // Dragging the thumb
        this.thumb.addEventListener("mousedown", (e) => {
            isDraggingThumb = true;
            dragOffsetX = (/** @type {MouseEvent} */(e)).clientX - this.thumb.getBoundingClientRect().left;
            document.body.style.userSelect = "none"; // Prevent text selection
        });

        /** @type {number | undefined} */
        let raf;
        document.addEventListener("mousemove", (e) => {
            if (!isDraggingThumb) return;
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                let newLeft = e.clientX - this.track.getBoundingClientRect().left - dragOffsetX;
                newLeft = Math.max(0, Math.min(newLeft, this.track.clientWidth - this.thumb.offsetWidth));
                this.thumb.style.left = `${newLeft}px`;

                const scrollRatio = newLeft / (this.track.clientWidth - this.thumb.offsetWidth);
                this.multicolumn.scrollLeft = scrollRatio * (this.multicolumn.scrollWidth - this.multicolumn.clientWidth);
            });
        });

        document.addEventListener("mouseup", () => {
            isDraggingThumb = false;
            document.body.style.userSelect = "";
            this.updateThumbPosition();
        });
    }

    mobileDragEventListeners() {
        let isDragging = false;
        let dragOffsetX = 0;
        // === Touch support for mobile ===
        this.thumb.addEventListener("touchstart", (e) => {
            const touch = e.touches[0];
            if (!touch) return;

            isDragging = true;

            // Calculate offset of touch inside thumb to prevent jump
            const thumbRect = this.thumb.getBoundingClientRect();
            dragOffsetX = touch.clientX - thumbRect.left;

            // Disable text selection/scrolling
            document.body.style.userSelect = "none";
            e.preventDefault();
        });

        /** @type {number | undefined} */
        let raf;
        document.addEventListener("touchmove", (e) => {
            if (!isDragging) return;

            const touch = e.touches[0];
            if (!touch) return;

            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                let newLeft = touch.clientX - this.track.getBoundingClientRect().left - dragOffsetX;
                newLeft = Math.max(0, Math.min(newLeft, this.track.clientWidth - this.thumb.offsetWidth));

                // Set thumb position using left
                this.thumb.style.left = `${newLeft}px`;

                // Update scroll proportionally
                const scrollRatio = newLeft / (this.track.clientWidth - this.thumb.offsetWidth);
                this.multicolumn.scrollLeft = scrollRatio * (this.multicolumn.scrollWidth - this.multicolumn.clientWidth);
            });

            e.preventDefault();
        }, { passive: false });

        document.addEventListener("touchend", () => {
            isDragging = false;
            document.body.style.userSelect = "";
            this.updateThumbPosition();
        });
    }

    updateThumbSize() {
        if (!this.multicolumn || !this.track || !this.thumb) return;
        const pageWidth = this.multicolumn.clientWidth; // width of one “page”
        const totalWidth = this.multicolumn.scrollWidth; // total content width
        const pages = totalWidth / pageWidth; // total pages

        // Thumb width proportional to visible page vs total pages
        let thumbWidth = this.track.clientWidth / pages;

        // Optional min-width
        const minWidth = 40;
        if (thumbWidth < minWidth) thumbWidth = minWidth;

        this.thumb.style.width = `${thumbWidth}px`;
    }

    updateThumbPosition() {
      const ratio = this.multicolumn.scrollLeft / (this.multicolumn.scrollWidth - this.multicolumn.clientWidth);
      this.thumb.style.left = `${ratio * (this.track.clientWidth - this.thumb.offsetWidth)}px`;
    };
}

if (!customElements.get('multicolumn-block')) {
  customElements.define('multicolumn-block', MulticolumnBlock);
}